/**
 * Care Coordination — unified member-record tab.
 *
 * Consolidates the previously-separate "Clinical–Social Coordination" and
 * "Health Connect" tabs into one tab with sub-navigation, so the member record
 * has a single Care Coordination home instead of overlapping peers.
 *   • Coordination  — the clinical↔social loop (triggers → referrals → hand-off →
 *                     loop-back), observations, and the clinician-gated AI insight.
 *   • Health Connect — connect/manage this member's health-data sources.
 * The Health Connect sub-tab is shown only when its view-setting is enabled.
 */
import { useState } from 'react';
import { ArrowRightCircle, HeartPulse, Settings2 } from 'lucide-react';
import ClinicalSocialCoordinationPanel from '@/components/v2/panels/ClinicalSocialCoordinationPanel';
import { HealthConnect } from '@/components/health/HealthConnect';

interface Props {
  memberId: number;
  showHealthConnect?: boolean;
  onNavigate?: (tab: string, memberId?: number) => void;
}

type Sub = 'coordination' | 'health-connect';

export default function CareCoordinationTab({ memberId, showHealthConnect = true, onNavigate }: Props) {
  const [sub, setSub] = useState<Sub>('coordination');

  const subTabs: { id: Sub; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'coordination', label: 'Coordination', icon: ArrowRightCircle },
    ...(showHealthConnect ? [{ id: 'health-connect' as const, label: 'Health Connect', icon: HeartPulse }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-v2border">
        {subTabs.map((tsub) => {
          const Icon = tsub.icon;
          const active = sub === tsub.id;
          return (
            <button
              key={tsub.id}
              onClick={() => setSub(tsub.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? 'border-primary text-primary' : 'border-transparent text-ink-500 hover:text-ink'
              }`}
            >
              <Icon className="h-4 w-4" /> {tsub.label}
            </button>
          );
        })}
        {onNavigate && (
          <button
            onClick={() => onNavigate('care-coordination-hub')}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm text-ink-500 hover:text-primary"
            title="Create/edit referral workflows for your organization"
          >
            <Settings2 className="h-4 w-4" /> Configure workflows
          </button>
        )}
      </div>

      {sub === 'coordination' && <ClinicalSocialCoordinationPanel memberId={String(memberId)} />}
      {sub === 'health-connect' && showHealthConnect && <HealthConnect fixedMemberId={memberId} />}
    </div>
  );
}
